import { useEffect, lazy, Suspense } from 'react';
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { I18nextProvider } from 'react-i18next';
import i18n from '@/i18n';
import NotFound from "./pages/NotFound";

const Index = lazy(() => import("./pages/Index"));


// Lazy load heavy route components
const EnergyAnalysis = lazy(() => import("./pages/EnergyAnalysis"));
const WaterAnalysis = lazy(() => import("./pages/WaterAnalysis"));


const queryClient = new QueryClient();

const App = () => {
  // One-time cleanup of the abandoned saved-reports feature's localStorage data.
  useEffect(() => {
    try {
      for (const key of ['energy_reports', 'energy_storage_config', 'energy_storage_stats']) {
        localStorage.removeItem(key);
      }
    } catch {
      // storage unavailable — nothing to clean
    }
  }, []);

  // Globally suppress the OS context menu on every chart (right-click on
  // desktop, long-press on touch). One capture-phase listener so it covers
  // every Recharts surface without per-component wiring or re-render cost.
  useEffect(() => {
    const onContextMenu = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target?.closest?.('.recharts-wrapper')) e.preventDefault();
    };
    document.addEventListener('contextmenu', onContextMenu, true);
    return () => document.removeEventListener('contextmenu', onContextMenu, true);
  }, []);


  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:text-sm">
            {i18n.t('accessibility.skipToContent')}
          </a>
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={
              <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">{i18n.t('app.loading')}</p>
                </div>
              </div>
            }>
              <Routes>
                <Route path="/" element={<Index />} />
                
                <Route path="/analysis" element={<EnergyAnalysis />} />
                <Route path="/water-analysis" element={<WaterAnalysis />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
};

export default App;
